import { yupResolver } from '@hookform/resolvers/yup';
import {
  Backdrop,
  Button,
  Container,
  Fade,
  makeStyles,
  Modal,
  TextField,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import * as ProjectService from '../../services/Projects';
import './NewTemplateModal.scss';
import { MessageActionType, MessageLevel } from '../../reducers/Message';
import { NewProjectRequest, ProjectStatus } from '../../models/Project';
import { AppState } from '../../reducers';
import { User, UserRole } from '../../models/User';
import { allUsers } from '../../services/Users';
import { Autocomplete } from '@material-ui/lab';

interface ModalProps {
  modalState: boolean;
  closeModal: () => void;
  finalize: (projectNumber: number) => void;
}

const useStyles = makeStyles({
  errorMsg: {
    color: 'red',
    fontSize: '0.75rem',
  },
  customerHeader: {
    marginBottom: 0,
  },
  buttonsContainer: {
    marginTop: '1rem',
  },
  cancel: {
    flexGrow: 1,
    marginRight: '',
  },
  submit: {
    float: 'right',
  },
});

const projectSchema = yup.object().shape({
  name: yup.string().required(),
  userId: yup.number().optional(),
});

const NewTemplateModal = (props: ModalProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(projectSchema),
  });

  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.auth.user);
  const [users, setUsers] = useState<User[]>([]);

  const onSubmit = async (formData: NewProjectRequest) => {
    const project = await ProjectService.createProject({
      ...formData,
      status: ProjectStatus.TEMPLATE,
    });
    if (project) {
      dispatch({
        type: MessageActionType.SetMessage,
        payload: {
          message: 'Malli luotu!',
        },
      });
      props.closeModal();
      props.finalize(project.projectNumber);
    } else {
      dispatch({
        type: MessageActionType.SetMessage,
        payload: {
          message: 'Mallin luonti epäonnistui!',
          level: MessageLevel.Error,
        },
      });
    }
    return;
  };

  useEffect(() => {
    if (props.modalState && user?.role !== UserRole.RETAILER) {
      const fetchUsers = async () => {
        try {
          const fetchedUsers = (await allUsers()).filter((u) => !!u.active);
          fetchedUsers.sort((a, b) => a.company.localeCompare(b.company));
          setUsers(fetchedUsers);
        } catch (err) {
          setUsers([]);
        }
      };

      fetchUsers();
    }
  }, [props.modalState, user?.role]);

  const getUserSelectionTitle = (user: User | undefined) => {
    if (user) {
      const { company, name, companyLevelAccess } = user;
      return `${company ? company + ' - ' : ''}${name}${
        companyLevelAccess ? ' *' : ''
      }`;
    }
    return '-';
  };

  const getOptionUser = (option: number | User | undefined) => {
    if (option) {
      if (typeof option === 'number') {
        option = users.find((u) => u.id === option) ?? user;
      } else {
        return option;
      }
    }
    return user;
  };

  const body = (
    <Fade in={props.modalState} timeout={500}>
      <div className="ProjectModal__Content">
        <form
          onSubmit={handleSubmit((d) => onSubmit(d as NewProjectRequest))}
          noValidate
        >
          <h2>Luo uusi mallipohja</h2>
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label="Nimi"
            type="text"
            id="name"
            error={errors.name ? true : false}
            {...register('name', { required: true })}
          />
          {user &&
            user?.role !== UserRole.RETAILER && [
              <h3 className={classes.customerHeader} key="jmh3">
                Jälleenmyyjä
              </h3>,
              <Autocomplete
                key="jmc"
                id="userId"
                defaultValue={user}
                options={users}
                getOptionLabel={(option) =>
                  getUserSelectionTitle(getOptionUser(option))
                }
                getOptionSelected={(option, value) => {
                  return option.id === getOptionUser(value)?.id;
                }}
                autoComplete={true}
                autoSelect={true}
                placeholder={getUserSelectionTitle(user)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    helperText="* = pääsy yrityksen tilauksiin"
                  />
                )}
                onChange={(
                  event: React.ChangeEvent<{}>,
                  value: User | null,
                ) => {
                  setValue(
                    'userId',
                    getOptionUser(value ?? user)?.id ?? user.id,
                  );
                }}
              />,
            ]}
          <Container disableGutters={true} className={classes.buttonsContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={props.closeModal}
              className={classes.cancel}
            >
              Peruuta
            </Button>
            <Button
              type="submit"
              value="submit"
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              Luo mallipohja
            </Button>
          </Container>
        </form>
      </div>
    </Fade>
  );

  return (
    <Modal
      className="ProjectModal"
      open={props.modalState}
      BackdropProps={{ timeout: 500 }}
      BackdropComponent={Backdrop}
    >
      {body}
    </Modal>
  );
};

export default NewTemplateModal;
